import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { csv, scaleLinear, scaleBand } from 'd3'
import { ChartAxisText, ChartNote } from 'components/styles'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'

export const ChartCh = () => {
  const t = useTranslation().t
  const theme = useTheme()
  const isMobile = window.innerWidth < 768
  const [data, setData] = useState<any>([])
  const svg = useRef<SVGSVGElement | null>(null)
  const svgContainer = useRef<HTMLDivElement | null>(null)
  const ratios = {
    desktop: 4 / 6,
    mobile: 2 / 1,
  }

  const [width, setWidth] = useState(0)
  const margin = {
    top: 5,
    right: 60,
    bottom: 20,
    left: 110,
  }
  const height =
    width * (isMobile ? ratios.mobile : ratios.desktop) -
    margin.top -
    margin.bottom

  // Y axis
  const scaleY = scaleBand()
    .range([0, height])
    .domain(data.map((d: any) => d.country))
    .padding(0.2)

  // X axis
  const scaleX = scaleLinear()
    .domain([
      0,
      Math.max.apply(
        null,
        data.map((item: any) => item.value)
      ),
    ])
    .range([0, width])

  const handleResize = () => {
    if (svgContainer.current) {
      setWidth(
        svgContainer.current?.getBoundingClientRect().width -
          margin.left -
          margin.right
      )
    }
  }

  useLayoutEffect(() => {
    handleResize()
  }, [svgContainer])

  useEffect(() => {
    csv(
      `${
        process.env.NODE_ENV === 'production'
          ? process.env.REACT_APP_DEPLOY_PATH
          : ''
      }/data/p90p100-world.csv`
    ).then((d: d3.DSVRowArray) => {
      setData(d.filter((item: any) => item.is_eu === 'True'))
    })

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const ticks = scaleX.ticks(isMobile ? 4 : 7)

  return (
    <div ref={svgContainer}>
      <svg
        ref={svg}
        width={width + margin.left + margin.right}
        height={height + margin.top + margin.bottom}
      >
        <g
          style={{ transform: `translate(${margin.left}px, ${margin.top}px)` }}
        >
          {/* Grid Y */}
          {data.map((item: any) => (
            <g style={{ transform: `translateY(${scaleY(item.country)}px)` }}>
              <line
                key={`grid-line-${item.country}`}
                x1={0}
                y1={0}
                x2={width}
                y2={0}
                stroke={theme.chartGridColorY}
                strokeDasharray='1px 2px'
              />
              <ChartAxisText
                x={-10}
                y={0}
                textAnchor='end'
                alignmentBaseline='middle'
              >
                {item.country === 'Switzerland' ? (
                  <tspan
                    alignmentBaseline='middle'
                    style={{
                      fill: 'var(--text-color)',
                      fontWeight: 'bold',
                    }}
                  >
                    {t(`worldChart:countries.${item.country}`)}
                  </tspan>
                ) : (
                  t(`worldChart:countries.${item.country}`)
                )}
              </ChartAxisText>
            </g>
          ))}

          {/* Grid X */}
          {ticks.map((tick: any, i: number) => (
            <g style={{ translate: scaleX(tick) }}>
              <line
                key={`grid-line-${tick}`}
                x1={0}
                y1={0}
                x2={0}
                y2={height - 10}
                stroke={theme.chartGridColorX}
              />
              <ChartAxisText x={0} y={height + 10} textAnchor='middle'>
                {tick}{' '}
                {i === ticks.length - 1 ? (
                  <>
                    tCO
                    <tspan baseline-shift='sub' font-size='smaller'>
                      2
                    </tspan>
                  </>
                ) : (
                  ''
                )}
              </ChartAxisText>
            </g>
          ))}

          {/* Bars */}
          {data.map((d: any) => {
            const y = scaleY(d.country) || 0

            return (
              <rect
                key={`bar-${d.country}`}
                onClick={() => {
                  console.log(d.country)
                }}
                width={scaleX(+d.value)}
                height={scaleY.bandwidth()}
                x={0}
                y={y - scaleY.bandwidth() / 2}
                fill={
                  d.country === 'Switzerland'
                    ? theme.brandColor
                    : theme.colorMode == 'dark'
                    ? '#333343'
                    : '#e2e4e7'
                }
              />
            )
          })}

          {/* <g
            style={{
              transform: `translate(${scaleX(40)}px, ${scaleY('Austria')}px)`,
            }}
          >
            <ChartNote>
              <tspan x={0}>L'épargne et les </tspan>
              <tspan x={0} dy={'1.2em'}>
                investissements expliquent
              </tspan>
              <tspan x={0} dy={'1.2em'}>
                une grande partie des
              </tspan>
              <tspan x={0} dy={'1.2em'}>
                écarts entre les pays.
              </tspan>
            </ChartNote>
            <g
              style={{
                transform: `translate(-50px, -35px) rotate(45deg)`,
              }}
            >
              <path
                style={{
                  stroke: 'var(--text-color)',
                  strokeWidth: 1,
                  fill: 'none',
                }}
                d='m64.27,18.1c-14.44,10.36-32.54,11.75-45.71,3.59C7.94,15.11,4.02,4.5,2.81.65'
              />
              <polyline
                style={{
                  stroke: 'var(--text-color)',
                  strokeWidth: 1,
                  fill: 'none',
                }}
                points='7.98 2.83 2.64 .65 .46 5.99'
              />
            </g>
          </g> */}

          {/* Objectif 2030 */}
          <g
            style={{
              transform: `translate(${scaleX(6) + 20}px, ${scaleY(
                'Portugal'
              )}px)`,
            }}
          >
            <ChartNote x={0} y={0} textAnchor='start'>
              {t('worldChart:objective')}
            </ChartNote>
            <g
              style={{
                transform: `translate(-20px, -55px) rotate(-135deg) scaleX(-1)`,
              }}
            >
              <path
                style={{
                  stroke: 'var(--text-color)',
                  strokeWidth: 1,
                  fill: 'none',
                }}
                d='m64.27,18.1c-14.44,10.36-32.54,11.75-45.71,3.59C7.94,15.11,4.02,4.5,2.81.65'
              />
              <polyline
                style={{
                  stroke: 'var(--text-color)',
                  strokeWidth: 1,
                  fill: 'none',
                }}
                points='7.98 2.83 2.64 .65 .46 5.99'
              />
            </g>
          </g>
          <line
            x1={scaleX(6)}
            y1={-10}
            x2={scaleX(6)}
            y2={height - 10}
            //strokeDasharray={'1px 1px'}
            strokeWidth={0.75}
            stroke={theme.brandColor}
          />
          <rect
            x={0}
            y={-10}
            width={scaleX(6)}
            height={height}
            fill={theme.brandColor}
            opacity={theme.colorMode == 'dark' ? 0.2 : 0.1}
          />
        </g>
      </svg>
    </div>
  )
}
